/**
 * AuthModal.jsx — sign-in window.
 *
 * Telegram: polling flow — site calls /auth/telegram/init → shows bot deep-link
 *           → user opens bot + sends /start → bot resolves state → polling detects.
 * Google:   OAuth popup flow — opens Google sign-in popup, receives id_token via postMessage
 *           from /google-callback.html.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api, getToken } from '../api/client';
import { TELEGRAM_BOT_USERNAME, GOOGLE_CLIENT_ID } from '../config';
import { t, useI18n } from '../hooks/useI18n';
import { desktopAuthHandoff } from '../utils/desktopAuthHandoff';
import { tr, Modal, Button } from '@quantum/ui';

const T = {
  panelBorder: 'color-mix(in srgb, var(--hi) 7%, transparent)',
  textPrimary: 'var(--hi)',
  textSecondary: 'var(--mid)',
  textTertiary: 'var(--low)',
  textQuiet: 'color-mix(in srgb, var(--hi) 20%, transparent)',
  up: '#3DDC97',
  fontMono: '"JetBrains Mono", monospace',
};

function TelegramIcon({ small }) {
  const s = small ? 14 : 18;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#5CB8FF" opacity="0.15" />
      <path d="M7 12.5 L17 7 L15 18 L10.5 14.5 L8.5 16 L8.5 13 L17 7" stroke="#5CB8FF" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" fill="none" />
    </svg>
  );
}
function GmailIcon({ small }) {
  const s = small ? 14 : 18;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="6" width="18" height="13" rx="2" stroke="#F2607F" strokeWidth="1.4" fill="#F2607F" fillOpacity="0.10" />
      <path d="M3.5 7.5 L12 13 L20.5 7.5" stroke="#F2607F" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    </svg>
  );
}
// ── Telegram polling sub-component ────────────────────────────────────────────
function TelegramPolling({ onDone, setAuthResult }) {
  const [botUrl, setBotUrl] = useState(null);
  const [state, setState] = useState(null);
  const [status, setStatus] = useState('init'); // init | waiting | success | error
  const pollRef = useRef(null);

  useEffect(() => {
    // 1. Init auth state on the server
    api('/auth/telegram/init', { method: 'POST', auth: false })
      .then((data) => {
        setState(data.state);
        setBotUrl(data.botUrl);
        setStatus('waiting');
      })
      .catch(() => setStatus('error'));

    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (status !== 'waiting' || !state) return;

    // 2. Poll until the bot resolves the state. Every 3s (the auth endpoint is
    // rate-limited; the status GET is exempt server-side but a slow poll is
    // still the polite default). A 429 / network blip is ignored — only a
    // real 404 (state gone) or the 9-minute cap ends it.
    const started = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - started > 9 * 60_000) {
        clearInterval(pollRef.current);
        setStatus('error');
        return;
      }
      try {
        const data = await api(`/auth/telegram/status?state=${state}`, { auth: false });
        if (data.status === 'ok') {
          clearInterval(pollRef.current);
          setStatus('success');
          setAuthResult({ token: data.token, user: data.user });
          onDone();
        }
      } catch (e) {
        if (e.status === 404) {
          clearInterval(pollRef.current);
          setStatus('error');
        }
        // 429 / 5xx / offline — keep polling, the bot may still confirm.
      }
    }, 3000);

    return () => clearInterval(pollRef.current);
  }, [status, state, onDone, setAuthResult]);

  const link = botUrl || `https://t.me/${TELEGRAM_BOT_USERNAME}`;
  let botName = TELEGRAM_BOT_USERNAME;
  if (!botName && botUrl) { try { botName = new URL(botUrl).pathname.slice(1); } catch { /* keep empty */ } }

  if (status === 'init') return (
    <div style={{ textAlign: 'center', color: T.textTertiary, fontFamily: T.fontMono, fontSize: 'var(--fs-xs)' }}>
      Initializing…
    </div>
  );

  if (status === 'error') return (
    <div style={{ textAlign: 'center', color: 'var(--bear)', fontFamily: T.fontMono, fontSize: 'var(--fs-xs)' }}>
      {tr('Error. Please try again.')}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <Button variant="secondary" icon={<TelegramIcon />} style={{ width: '100%' }} onClick={() => window.open(link, '_blank', 'noopener')}>
        {botName ? `Open @${botName}` : tr('Open Telegram')}
      </Button>
      {/* In-app browsers open t.me in the same tab and the polling page is lost —
          the link as plain text, to open it in Telegram by hand. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <code style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'all', fontFamily: T.fontMono, fontSize: 'var(--fs-xs)', color: 'var(--mid)' }}>{link}</code>
        <Button variant="ghost" size="sm" onClick={() => navigator.clipboard?.writeText(link)}>{tr('Copy')}</Button>
      </div>

      {/* Waiting indicator */}
      {status === 'waiting' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.textTertiary, fontFamily: T.fontMono, fontSize: 'var(--fs-micro)', letterSpacing: 0.6 }}>
          <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'var(--info)', animation: 'pulse 1.4s ease-in-out infinite' }} />
          {tr('WAITING FOR BOT CONFIRMATION…')}
        </div>
      )}
    </div>
  );
}

// ── Google popup sub-component ────────────────────────────────────────────────
function GoogleLogin({ onDone, loginGoogle }) {
  const [status, setStatus] = useState('idle'); // idle | waiting | success | error
  const popupRef = useRef(null);
  const listenerRef = useRef(null);
  const timerRef = useRef(null);

  const cleanup = () => {
    if (listenerRef.current) { window.removeEventListener('message', listenerRef.current); listenerRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (popupRef.current && !popupRef.current.closed) { popupRef.current.close(); }
    popupRef.current = null;
  };

  useEffect(() => () => cleanup(), []);

  const handleOpen = () => {
    if (!GOOGLE_CLIENT_ID) { setStatus('error'); return; }
    setStatus('waiting');

    const nonce = Math.random().toString(36).slice(2);
    const redirectUri = `${window.location.origin}/google-callback.html`;
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce,
      prompt: 'select_account',
    });

    popupRef.current = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
      'google-auth',
      'width=500,height=600,left=300,top=100'
    );

    listenerRef.current = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'google_auth') return;
      cleanup();
      loginGoogle(event.data.credential)
        .then(() => { setStatus('success'); onDone(); })
        .catch(() => setStatus('error'));
    };
    window.addEventListener('message', listenerRef.current);

    timerRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        cleanup();
        setStatus((s) => s === 'waiting' ? 'idle' : s);
      }
    }, 500);
  };

  if (status === 'error') return (
    <div style={{ textAlign: 'center', color: 'var(--bear)', fontFamily: T.fontMono, fontSize: 'var(--fs-xs)' }}>
      {GOOGLE_CLIENT_ID ? tr('Error. Please try again.') : tr('Google OAuth is not configured.')}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <Button variant="secondary" icon={<GmailIcon />} style={{ width: '100%' }} disabled={status === 'waiting'} onClick={handleOpen}>
        {t('auth.continue_gm')}
      </Button>

      {status === 'waiting' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.textTertiary, fontFamily: T.fontMono, fontSize: 'var(--fs-micro)', letterSpacing: 0.6 }}>
          <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'var(--bear)', animation: 'pulse 1.4s ease-in-out infinite' }} />
          {tr('WAITING FOR EMAIL CONFIRMATION…')}
        </div>
      )}
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────
export default function AuthModal({ onClose }) {
  useI18n();
  const { loginGoogle, setAuthResult, error } = useAuth();
  const [method, setMethod] = useState(null);

  // Desktop-auth handoff — see utils/desktopAuthHandoff.js for the full
  // story (also called from App.jsx for an already-signed-in visitor).
  const done = () => {
    if (desktopAuthHandoff(getToken())) return;
    onClose?.();
  };

  return (
    <Modal title={t('auth.welcome')} onClose={onClose || (() => {})} width={460}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, marginBottom: 22 }}>
        {/* The real logo, from the one file that defines it. */}
        <div style={{
          width: 56, height: 56, borderRadius: 16, background: 'var(--s3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 12px 30px color-mix(in srgb, var(--brand) 22%, transparent)',
        }}>
          <img src="/favicon.svg" width={30} height={30} alt="Quantum" />
        </div>
        <div style={{ fontFamily: T.fontMono, fontSize: 'var(--fs-xs)', color: T.textTertiary, letterSpacing: 0.8, marginTop: 8 }}>{tr('FREE DURING BETA · FULL ACCESS')}</div>
      </div>

      {!method ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Button variant="secondary" icon={<TelegramIcon />} onClick={() => setMethod('telegram')}>{t('auth.continue_tg')}</Button>
          <Button variant="secondary" icon={<GmailIcon />} onClick={() => setMethod('gmail')}>{t('auth.continue_gm')}</Button>
          {error && <div style={{ color: 'var(--bear)', fontFamily: T.fontMono, fontSize: 'var(--fs-xs)', textAlign: 'center' }}>{error}</div>}
          <div style={{ marginTop: 6, padding: '12px 14px', background: 'var(--s2)', borderRadius: 10, fontFamily: T.fontMono, fontSize: 'var(--fs-micro)', color: T.textTertiary, letterSpacing: 0.4, lineHeight: 1.6, textAlign: 'center' }}>
            {tr('By continuing, you agree to our')}{' '}
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--bull)', textDecoration: 'underline', textUnderlineOffset: 2 }}>{tr('privacy policy')}</a>.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--s2)', borderRadius: 10 }}>
            {method === 'telegram' ? <TelegramIcon small /> : <GmailIcon small />}
            <span style={{ fontSize: 'var(--fs-sm)', color: T.textSecondary, letterSpacing: 0.3 }}>{tr('Signing in via {m}', { m: method === 'telegram' ? 'Telegram' : 'Gmail' })}</span>
            <div style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" onClick={() => setMethod(null)}>{t('auth.change')}</Button>
          </div>
          {method === 'telegram' && <TelegramPolling onDone={done} setAuthResult={setAuthResult} />}
          {method === 'gmail' && <GoogleLogin onDone={done} loginGoogle={loginGoogle} />}
        </div>
      )}
    </Modal>
  );
}
